import { HttpInterceptorFn } from '@angular/common/http';
import { environment } from '../../environments/environment';

/**
 * Maps frontend API paths to their Java backend equivalents.
 *
 * Frontend uses paths like /passengers, /trips, /categories —
 * the Java API uses /users, /admin/trips, /car-categories, etc.
 *
 * Optional 3rd element constrains the rewrite to a specific HTTP method.
 */

type PathRewrite = [RegExp, string] | [RegExp, string, string];

const PATH_REWRITES: PathRewrite[] = [
  // Users / Passengers
  [/^\/passengers(?=\/|$)/, '/users'],

  // Admin CRUD
  [/^\/ratings(?=\/|$)/, '/admin/ratings'],
  [/^\/trips(?=\/|$)/, '/admin/trips'],
  [/^\/promos(?=\/|$)/, '/admin/promos'],
  [/^\/categories(?=\/|$)/, '/car-categories'],
  [/^\/driver-fees(?=\/|$)/, '/admin/driver-fees'],
  [/^\/fare-configs(?=\/|$)/, '/admin/fare-configs'],
  [/^\/compliance(?=\/|$)/, '/admin/compliance'],

  // Geo
  [/^\/geo\/countries(?=\/|$)/, '/admin/countries'],
  [/^\/geo\/states(?=\/|$)/, '/admin/states'],
  [/^\/geo\/cities(?=\/|$)/, '/admin/cities'],

  // Geofence
  [/^\/service-zones(?=\/|$)/, '/geofence/service-zones'],

  // Driver approval
  [/^\/drivers\/approve$/, '/auth/approve'],

  // Payments — Java uses non-REST paths (method-specific)
  [/^\/payments\/driver$/, '/payment-summary/driver'],
  [/^\/payments\/(\d+)$/, '/payments/transaction/$1', 'GET'],
  [/^\/payments$/, '/payments/list', 'GET'],

  // Documents — Java uses non-REST paths (method-specific)
  [/^\/documents\/add$/, '/documents/add', 'POST'],
  [/^\/documents$/, '/documents/list', 'GET'],
  [/^\/documents$/, '/documents/edit', 'PUT'],

  // Dashboard stats
  [/^\/dashboard\/stats$/, '/admin/stats/dashboard'],

  // Team management — SPECIFIC sub-resources first (order matters: the generic
  // /team rule below would otherwise rewrite these to /admin/team/members/...).
  [/^\/team\/permissions$/, '/admin/team/permissions'],
  [/^\/team\/roles$/, '/admin/team/roles'],
  [/^\/team\/audit-trail(?=\/|$|\?)/, '/admin/team/audit-trail'],
  [/^\/team\/activity(?=\/|$|\?)/, '/admin/team/activity'],
  [/^\/team\/members(?=\/|$|\?)/, '/admin/team/members'],
  // Per-member sub-resources: /team/:id/role|status|permissions|activity
  [/^\/team\/(\d+)\/role$/, '/admin/team/members/$1/role'],
  [/^\/team\/(\d+)\/status$/, '/admin/team/members/$1/status'],
  [/^\/team\/(\d+)\/permissions$/, '/admin/team/members/$1/permissions'],
  [/^\/team\/(\d+)\/activity(?=\/|$|\?)/, '/admin/team/members/$1/activity'],
  // Generic: list (/team) and single member (/team/:id) → /admin/team/members[/:id]
  [/^\/team(?=\/|$)/, '/admin/team/members'],

  // Vendors (list, CRUD, sub-resources like /vendors/:id/finance)
  [/^\/vendors(?=\/|$)/, '/admin/vendors'],

  // Activity / Audit log
  [/^\/activity\/steps$/, '/admin/team/activity'],
];

export const apiPathMapperInterceptor: HttpInterceptorFn = (req, next) => {
  const apiBase = environment.apiBaseUrl;
  if (!req.url.startsWith(apiBase)) return next(req);

  const path = req.url.substring(apiBase.length).split('?')[0];
  const query = req.url.substring(apiBase.length + path.length);

  for (const rule of PATH_REWRITES) {
    const [pattern, replacement] = rule;
    const method = rule.length === 3 ? rule[2] : undefined;

    if (method && req.method !== method) continue;
    if (pattern.test(path)) {
      const newPath = path.replace(pattern, replacement);
      const newUrl = `${apiBase}${newPath}${query}`;
      return next(req.clone({ url: newUrl }));
    }
  }

  return next(req);
};
